<template>
  <div class="notes-bar">
    <div class="notes-header grid-x align-middle">
      <div class="cell auto align-self-middle text-center">
        <h5>
          {{ $t('processing_notes_title') }}
        </h5>
      </div>
    </div>
    <div v-if="this.loading" class="text-center loader">
      <BeatLoader v-if="this.loading" />
    </div>
    <div
      v-else-if="this.error"
      class="text-center"
    >
      {{ $t('loadingError') }}
    </div>
    <div v-else class="notes-content">
      <ul id="accordion" class="vertical menu accordion-menu" data-accordion-menu>
        <li v-for="(group, noteIdx) in this.groupedNotes" :key="noteIdx" class="note-wrapper">
          <a class="note-title" href="#">
            <div class="title-content">
              <i
                v-if="group.priority === 'critical'"
                class="fas fa-minus-circle critical-overview text-center"
              />
              <i
                v-if="group.priority === 'release_required'"
                class="fas fa-headset internal-overview text-center"
              />
              <i
                v-if="group.priority === 'warning'"
                class="fas fa-exclamation-triangle warning-overview text-center"
              />
              <i
                v-if="group.priority === 'relevant'"
                class="fas fa-info-circle relevant-overview text-center"
              />
              <i
                v-if="group.priority === 'internal'"
                class="fas fa-glasses internal-overview text-center"
              />
              {{ $t(group.category) }}
            </div>
          </a>
          <ul class="menu vertical nested note-cases">
            <li>
              <div>
                <p
                  class="notes-description"
                  v-if="$te((group.category + '_description'), 'en')"
                  v-html="$t(group.category + '_description')"
                />
              </div>
            </li>
            <li v-for="(note, index) in group.notes" :key="index">
              <a
                v-if="note.priority !== 'critical'"
                @click="drawNoteShape(note)"
                class="note-case"
                :class="{ 'drawn': note.selected }"
                href="#"
              >
                <div class="case-text">
                  <div v-if="note.identifiers.length === 0" class="case-number">
                    Case #{{ index + 1 }}
                  </div>
                  <div v-else class="case-number">{{ note.identifiers.join(", ") }}</div>
                  <div v-if="note.shapes.length !== 0">
                    <i v-if="note.selected" class="indicator fas fa-eye" />
                    <i v-else class="indicator fas fa-eye-slash" />
                  </div>
                </div>
              </a>
              <hr class="subtle--full">
            </li>
          </ul>
        </li>
      </ul>
      <div v-if="this.notes.length === 0" class="vertical menu no-notes">
        <div>
          <div class="cell">
            <div class="text-center">
              <i class="fas fa-3x fa-check-circle success-overview text-center" />
              <div>{{ $t('niceDesign') }}</div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="this.notes.length > 0"
        class="analyze-notice"
      >
        {{ this.$t('notesDescription') }}
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery';
import Foundation from 'foundation-sites';
import { getRequest } from 'helper/request';
import { components } from 'locales';
import BeatLoader from 'components/shared/BeatLoader.vue';

export default {
  name: 'BoardNotesBar',
  components: { BeatLoader },
  props: {
    metaPath: String,
  },
  data() {
    return {
      notes: [],
      groupedNotes: [],
      bus: this.$root.bus,
      loading: false,
      error: null,
    };
  },
  computed: {
    criticalNotesCount() {
      return this.groupedNotes.filter((group) => group.priority === 'critical').length;
    },
    warningNotesCount() {
      return this.groupedNotes.filter((group) => group.priority === 'warning').length;
    },
    relevantNotesCount() {
      return this.groupedNotes.filter((group) => group.priority === 'relevant').length;
    },
    internalNotesCount() {
      return this.groupedNotes.filter((group) => group.priority === 'internal').length;
    },

  },
  methods: {
    loadMeta() {
      getRequest(this.metaPath).then((data) => {
        this.notes = data.notes.map((note) => {
          note.selected = false;
          return note;
        });
        this.groupNotes();
        this.loading = false;
      }).catch((e) => {
        this.loading = false;
        this.error = e;
      });
    },
    groupNotes() {
      const groupedNotes = [];

      this.notes.forEach((note) => {
        const noteGroup = groupedNotes.find((group) => group.category === note.category);
        if (noteGroup) {
          noteGroup.notes.push(note);
        } else {
          groupedNotes.push({
            category: note.category,
            priority: note.priority,
            notes: [note],
          });
        }
      });
      this.groupedNotes = groupedNotes.sort((group1, group2) => {
        const priorities = {
          critical: 1,
          release_required: 2,
          warning: 3,
          relevant: 4,
          internal: 5,
        };
        return priorities[group1.priority] - priorities[group2.priority];
      });
    },
    drawNoteShape(note) {
      if (note.shapes.length <= 0) {
        return;
      }
      note.selected = !note.selected;
      const method = note.selected ? 'addNote' : 'removeNote';
      this.bus.emit(method, note);
    },
  },
  mounted() {
    this.loading = true;
    this.loadMeta();
  },
  updated() {
    this.$nextTick(function () {
      // Foundation requires jQuery reference
      this.accordion = new Foundation.AccordionMenu($('.accordion-menu'));
    });
  },
  unmounted() {
    this.accordion.destroy();
  },

  i18n: components.boards.BoardNotesBar,
};
</script>

<style scoped lang="scss">
@import 'stylesheets/foundation/colors';

.notes-header {
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 0.75rem;

  h5 {
    margin: 0;
  }
}

.note-title {
  color: black;
  border-top-style: solid;
  border-top-width: 1px;
  border-color: #acacac;
  min-height: 54px;
  display: unset;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;

  i {
    margin-right: 0.5rem;
  }

  &:hover {
    color: black;
  }

  &:focus {
    color: black;
  }

  .title-content {
    display: flex;
    flex-direction: row;
    column-gap: 0.25rem;
    align-items: center;
    margin-right: calc(6px + 0.25rem);
  }
}

.note-wrapper:last-child {
  border-bottom-style: solid;
  border-color: #acacac;
  border-bottom-width: 1px;
}

.note-case {
  border: 1px solid transparent;
  color: grey;
  background-color: white;
  flex-direction: column;
  justify-content: center;

  .case-text {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &:first-child {
    border-top-style: solid;
    border-top-color: #acacac;
    border-top-width: 1px;
  }

  &:hover {
    color: black;
    background-color: #cacaca
  }

  &:focus {
    color: grey;
  }
}

.note-cases {
  margin-left: 0 !important;
  color: black;

  hr {
    width: 100%;
    margin: 0 !important;
  }

  .indicator {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .case-number {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.drawn {
  color: black !important;
  background-color: #cacaca !important;
}

.notes-overview {
  padding-top: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 12%;
  font-size: 1.2rem;

  i {
    font-size: 1.2rem;
  }

  div {
    display: flex;
    flex-direction: column;
  }
}

.critical-overview {
  color: map-get($foundation-palette, 'danger');
}

.warning-overview {
  color: map-get($foundation-palette, 'warning');
}

.relevant-overview {
  color: $blue;
}

.internal-overview {
  color: $dark-gray;
}

.success-overview {
  color: $green
}

.notes-description {
  padding: 0.7rem 1rem;
  font-size: small;
  margin-bottom: 0;

  a {
    display: unset !important;
    padding: 0 !important;
  }
}

.analyze-notice {
  padding: 1rem;
  font-size: 0.9rem;
}

.no-notes {
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  border-top-style: solid;
  border-top-width: 1px;
  border-color: #b2b2b2;

  i {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
}

</style>
